import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ipconfig from "../../Core/ipconfig";
import {
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Typography,
  TableContainer,
  Paper,
  useTheme,
} from "@mui/material";
import Spinner from "../../Shared/Feature/LoadingSpinner/Spinner";
import { toast } from "react-toastify";

export default function PartnerConnectionRequests({ partnerId }: { partnerId: string }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${ipconfig}/partner/connection-requests?partnerId=${partnerId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setRequests(res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      await axios.put(
        `${ipconfig}/partner/connection-request/update?requestId=${id}`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success(`Request status updated to ${status}`);
      fetchRequests();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  };

  useEffect(() => {
    if (partnerId) {
      fetchRequests();
    }
  }, [partnerId]);

  if (loading) {
    return <Spinner height="200px" />;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", height: "100%" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary" }}>
        Connection Requests ({requests.length})
      </Typography>

      {requests.length > 0 ? (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            borderRadius: "12px",
            maxHeight: "350px",
            "&::-webkit-scrollbar": {
              width: "5px",
              height: "5px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "divider",
              borderRadius: "4px",
            },
          }}
        >
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b" }}>Plan</TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b", textAlign: "center", width: 220 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((r, idx) => (
                <TableRow
                  key={r._id || idx}
                  sx={{
                    "&:hover": {
                      backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "#1e293b50",
                    },
                    transition: "all 0.15s ease",
                  }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{r.customerName}</TableCell>
                  <TableCell
                    onClick={(e) => {
                      e.stopPropagation();
                      if (r.customerEmail) {
                        navigate("/user", { state: { email: r.customerEmail, search: r.customerEmail } });
                      }
                    }}
                    sx={{
                      color: r.customerEmail ? "primary.main" : "text.primary",
                      cursor: r.customerEmail ? "pointer" : "default",
                      fontWeight: r.customerEmail ? 600 : 400,
                      "&:hover": r.customerEmail ? { textDecoration: "underline" } : {}
                    }}
                  >
                    {r.customerEmail}
                  </TableCell>
                  <TableCell>
                    <Chip label={r.plan || "Free"} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                      {r.status === "Pending" ? (
                        <Box sx={{ display: "flex", gap: "8px" }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() => updateStatus(r._id, "Approved")}
                            sx={{ textTransform: "none", fontWeight: 600 }}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => updateStatus(r._id, "Rejected")}
                            sx={{ textTransform: "none", fontWeight: 600 }}
                          >
                            Reject
                          </Button>
                        </Box>
                      ) : (
                        <Chip
                          size="small"
                          label={r.status}
                          color={r.status === "Approved" ? "success" : "error"}
                          sx={{ fontWeight: 700, fontSize: "11px" }}
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ p: 4, textAlign: "center", border: "1px dashed", borderColor: "divider", borderRadius: "12px" }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
            No connection requests found for this partner.
          </Typography>
        </Box>
      )}
    </Box>
  );
}